'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@/primitives';
import {
  BREEDPLAN_ANIMAL_SELECTION_URL,
  BREEDPLAN_INTERPRETING_EBVS_URL,
} from './breedplanInterpretationLinks';

type BreedplanInterpretationInfoModalProps = {
  open: boolean;
  onClose: () => void;
};

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="titlemedium"
      component="h3"
      gutterBottom
      sx={{ fontWeight: 700 }}
    >
      {children}
    </Typography>
  );
}

export default function BreedplanInterpretationInfoModal({
  open,
  onClose,
}: BreedplanInterpretationInfoModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{ paper: { sx: { mb: { xs: 2 } } } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
        }}
      >
        Interpreting and using BREEDPLAN information
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <SectionHeading>Interpreting BREEDPLAN EBVs</SectionHeading>
            <Typography variant="bodymedium" paragraph>
              Only BREEDPLAN EBVs produced in the same BREEDPLAN analysis should
              be directly compared. Each BREEDPLAN EBV is reported in the units
              in which the measurements are taken (e.g. Weight EBVs are reported
              in kilograms).
            </Typography>
            <Typography variant="bodymedium" paragraph>
              Compare to current levels of genetic merit by using breed averages
              and EBV percentiles. For many traits, it is generally more
              desirable to be in the top percentiles. However, the most
              appropriate percentile depends on breeding objectives and
              production environment. Traits such as Milk, Mature Cow Weight,
              Birth Weight, Rib Fat and Rump Fat are examples where selecting
              animals within an optimal or moderate range may be preferable.
            </Typography>
            <Typography variant="bodymedium" paragraph>
              Consider expected progeny performance. On average, half of the
              difference in the EBVs will be passed on to the progeny. For
              example, consider the 400 Day Weight EBVs of Sire A (+52) and Sire
              B (+32). On average, the progeny of Sire A will be 10 kg heavier
              than the progeny of Sire B at 400 days of age.
            </Typography>
            <Typography variant="bodymedium" paragraph>
              Consider EBV accuracy (%). As a general rule, animals should be
              compared on EBVs regardless of accuracy. Young sale bulls usually
              have similar (lower) EBV accuracies, while older AI sires with
              many progeny have higher EBV accuracies. The choice depends on
              your risk tolerance and mating situation (e.g. you may choose
              higher accuracy Calving Ease EBV bulls for first-calf heifers).
            </Typography>
            <Typography variant="bodymedium">
              For further information, please see the BREEDPLAN Guide to
              Interpreting EBVs tip sheet and short video.{' '}
              <Link
                href={BREEDPLAN_INTERPRETING_EBVS_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 500 }}
              >
                BREEDPLAN - A BREEDPLAN Guide to Interpreting EBVs (with video)
              </Link>
            </Typography>
          </Box>

          <Box>
            <SectionHeading>
              Using BREEDPLAN Information in Animal Selection
            </SectionHeading>
            <Typography variant="bodymedium" paragraph>
              BREEDPLAN information should be considered alongside other animal
              selection criteria, including visual assessment.
            </Typography>
            <Typography variant="bodymedium" paragraph>
              The approach recommended below helps beef producers identify
              animals whose genetics best match their production system and
              profitability goals, enabling more informed and balanced
              decisions:
            </Typography>
            <Box
              component="ol"
              sx={{
                m: 0,
                pl: 2.5,
                '& li': { mb: 1 },
                typography: 'bodymedium',
              }}
            >
              <li>Identify the selection index of most relevance to you.</li>
              <li>Rank animals using the chosen selection index.</li>
              <li>Consider the individual BREEDPLAN EBVs of importance.</li>
              <li>
                Consider other traits of importance, including pedigree, genetic
                condition status, structural soundness, fertility and
                temperament.
              </li>
            </Box>
            <Typography variant="bodymedium" paragraph sx={{ mt: 2 }}>
              With much of this information available online, these steps can
              largely be completed before sale day, allowing a shortlist of
              genetically suitable animals to be prioritised for visual
              inspection on sale day.
            </Typography>
            <Typography variant="bodymedium">
              For further information, please see the BREEDPLAN Guide to Animal
              Selection tip sheet and short video.{' '}
              <Link
                href={BREEDPLAN_ANIMAL_SELECTION_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 500 }}
              >
                BREEDPLAN - A BREEDPLAN Guide to Animal Selection (with video)
              </Link>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
